import type { AssignableUser, LeadDistribution } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Spinner, Switch } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { assignableQuery } from '../crm/queries.js';
import { api } from '../lib/api.js';
import { SectionHeader } from './layout.js';

const distributionQuery = queryOptions({ queryKey: ['lead-distribution'], queryFn: () => api<LeadDistribution>('/lead-distribution') });

/** Configurações → Distribuição de leads: rodízio entre as pessoas escolhidas. */
export function DistributionSettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery(distributionQuery);
  const { data: people } = useQuery(assignableQuery);
  const [enabled, setEnabled] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => { if (data) { setEnabled(data.enabled); setSelected(data.userIds); } }, [data]);
  const save = useMutation({
    mutationFn: () => api<LeadDistribution>('/lead-distribution', { method: 'PUT', body: { enabled, userIds: selected } }),
    onSuccess: (res) => qc.setQueryData(distributionQuery.queryKey, res),
  });
  if (!data || !people) return <Spinner className="size-5 text-fg-subtle" label="Carregando" />;
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const next = people.find((p: AssignableUser) => p.id === data.nextUserId);
  const dirty = enabled !== data.enabled || selected.join() !== data.userIds.join();

  return (
    <div>
      <SectionHeader title="Distribuição de leads" description="Cada lead novo que chega pelo WhatsApp ganha um responsável na hora, em rodízio. A conversa aparece em “Minhas” para essa pessoa; o assistente virtual segue atendendo até ela assumir." />
      <Card className="mt-6 p-5">
        <Switch checked={enabled} onChange={setEnabled} label="Rodízio automático"
          description={data.enabled && next ? `Próximo lead vai para ${next.name}.` : 'Desligado: os leads chegam sem responsável.'} />
        <p className="mt-6 text-[13px] font-medium text-fg">Quem entra no rodízio (na ordem da lista)</p>
        <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
          {people.map((p: AssignableUser) => (
            <li key={p.id}>
              <label className="flex h-11 cursor-pointer items-center gap-3 rounded-lg border border-border px-3 hover:border-border-strong">
                <input type="checkbox" className="size-4 accent-[var(--dnx-primary)]" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
                <span className="flex-1 text-sm text-fg">{p.name}</span>
                {selected.includes(p.id) && <Badge>{selected.indexOf(p.id) + 1}º</Badge>}
              </label>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[12.5px] text-fg-muted">Só aparecem pessoas de administração, gestão, comercial e recepção. Quem for desativado sai do rodízio sozinho.</p>
        {save.error && <Alert tone="danger" className="mt-4">{save.error.message}</Alert>}
        {save.isSuccess && !dirty && <Alert tone="success" className="mt-4">Distribuição salva.</Alert>}
        <div className="mt-5 flex justify-end border-t border-border pt-5">
          <Button loading={save.isPending} disabled={!dirty || (enabled && !selected.length)} onClick={() => save.mutate()}>Salvar</Button>
        </div>
      </Card>
    </div>
  );
}
